import React, { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { MessageSquare, CheckCircle2 } from 'lucide-react';
import { MessageData } from '../../hooks/use-messages';

interface MessageListProps {
  messages: MessageData[];
  selectedId: string | null;
  onSelectMessage: (id: string) => void;
  isLoading: boolean;
}

/**
 * MessageList component displays a list of messages (grouped conceptually)
 * Allows selecting a message to view its thread/details
 */
const MessageList: React.FC<MessageListProps> = ({
  messages,
  selectedId,
  onSelectMessage,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 h-full">
        <div className="w-8 h-8 border-4 border-t-[var(--brand-primary)] border-r-[var(--brand-primary)] border-b-[var(--background-surface)] border-l-[var(--background-surface)] rounded-full animate-spin" />
        <p className="text-[var(--text-secondary)] mt-4">Loading messages...</p>
      </div>
    );
  }

  // Group messages by sender/recipient pairs to simulate conversations
  // This logic might need refinement based on how conversations are defined
  const groupedMessages = useMemo(() => {
    const groups: Record<string, MessageData[]> = {};
    messages.forEach(message => {
      // Simple grouping by sender for now, assumes sender ID identifies the conversation partner
      // More complex grouping might involve both sender/recipient IDs
      const partnerId = message.sender_id; // Or determine partner based on user context
      if (!groups[partnerId]) {
        groups[partnerId] = [];
      }
      groups[partnerId].push(message);
    });
    // Sort each group by timestamp descending to get the latest message first
    Object.values(groups).forEach(group => group.sort((a, b) => parseISO(b.timestamp).getTime() - parseISO(a.timestamp).getTime()));
    return groups;
  }, [messages]);

  const conversationPreviews = useMemo(() => {
    return Object.values(groupedMessages).map(group => {
      const latestMessage = group[0]; // The first message after sorting is the latest
      // Determine unread status based on the latest message (or count unread in group)
      const isUnread = !latestMessage.read; 
      // In a real app, you might count unread messages in the group
      // const unreadCount = group.filter(m => !m.read).length;
      return {
        id: latestMessage.id, // Use latest message ID for selection (or a generated group ID)
        partnerName: latestMessage.sender_name, // Use sender name as partner name
        partnerRole: latestMessage.sender_role,
        subject: latestMessage.subject || 'No Subject', // Use subject from latest message
        lastMessagePreview: latestMessage.content,
        lastMessageTimestamp: latestMessage.timestamp,
        isUnread: isUnread,
        // unreadCount: unreadCount, // Add if implementing unread count
        // isUrgent: latestMessage.is_urgent, // Add if urgent flag exists and is needed
      };
    }).sort((a, b) => parseISO(b.lastMessageTimestamp).getTime() - parseISO(a.lastMessageTimestamp).getTime()); // Sort previews by latest message time
  }, [groupedMessages]);

  if (conversationPreviews.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 h-full">
        <div className="w-16 h-16 rounded-full bg-[var(--background-subtle)] flex items-center justify-center mb-4">
          <MessageSquare className="h-8 w-8 text-[var(--text-secondary)]" />
        </div>
        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No messages</h3>
        <p className="text-center text-[var(--text-secondary)]">
          You don't have any messages yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      {conversationPreviews.map((preview) => {
        const isSelected = preview.id === selectedId;
        const isUnread = preview.isUnread;
        
        // Attempt to parse the timestamp safely
        let formattedTime = 'Invalid Date';
        try {
          const parsedDate = parseISO(preview.lastMessageTimestamp);
          formattedTime = format(parsedDate, 'MMM d');
        } catch (e) {
          console.error("Error parsing date:", preview.lastMessageTimestamp, e);
        }

        return (
          <div
            key={preview.id}
            onClick={() => onSelectMessage(preview.id)}
            className={`
              p-4 border-b border-[var(--border-subtle)] cursor-pointer 
              hover:bg-[var(--background-interactive)] 
              transition-colors duration-200
              relative
              ${isSelected ? 
                'bg-blue-50/60 dark:bg-blue-900/40 border-l-4 border-l-blue-500 dark:border-l-blue-400' : 
                isUnread ? 
                  'bg-[var(--brand-primary-subtle)]' : 
                  'bg-transparent'
              }
              ${isSelected ? 'pl-[calc(1rem-4px)]' : ''}
            `}
          >
            {isSelected && (
              <div className="absolute inset-y-0 left-0 w-1 bg-blue-500 dark:bg-blue-400"></div>
            )}
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full bg-[var(--background-subtle)] flex items-center justify-center mr-3 ${isSelected ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`}>
                  <span className="text-[var(--text-secondary)] font-semibold">
                    {preview.partnerName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isSelected ? 'text-blue-600 dark:text-blue-300' : isUnread ? 'text-[var(--brand-primary)]' : 'text-[var(--text-primary)]'}`}>
                    {preview.partnerName}
                  </h3>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {preview.partnerRole}
                  </p>
                </div>
              </div>
              <div className="text-xs text-[var(--text-secondary)]">
                {formattedTime}
              </div>
            </div>
            
            <div className="mb-2">
              <h4 className={`text-sm font-medium ${isSelected ? 'text-blue-800 dark:text-blue-200' : isUnread ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
                {preview.subject}
              </h4>
            </div>
            
            <div className="flex items-start">
              <p className={`text-sm line-clamp-2 ${isSelected ? 'text-blue-900/80 dark:text-blue-100/80' : isUnread ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
                {preview.lastMessagePreview}
              </p>
            </div>
            
            <div className="flex justify-between items-center mt-2">
              {isUnread ? (
                <div className="flex items-center">
                  <span className="w-2 h-2 rounded-full bg-[var(--brand-primary)]"></span>
                  <span className="text-xs text-[var(--brand-primary)] ml-1">
                    New
                  </span>
                </div>
              ) : isSelected && (
                <div className="flex items-center">
                  <CheckCircle2 className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-xs text-blue-500 dark:text-blue-400 ml-1">Selected</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageList; 